import { useEffect, useRef, useState, type SetStateAction } from "react";
import { draftOwner } from "@/shared/lib/drafts";

/** How one kind of draft is kept (`@/shared/lib/drafts`). */
export interface DraftKind<T> {
  empty: T;
  /** What is kept under a key; `empty` when nothing is. */
  read: (key: string) => T;
  /** Keeps it in place of what was there; `empty` removes the entry. */
  write: (key: string, draft: T) => void;
  /** Throws it away, previews and all. */
  discard: (key: string) => void;
  /** A refused send's draft, back in front of what was typed since. */
  merge: (refused: T, now: T) => T;
}

/** What a send took out of the composer, held until the send answers (`settle`). */
export interface Taken<T> {
  key: string;
  owner: string;
  draft: T;
}

interface Held<T> {
  key: string;
  draft: T;
  /** Write it through; false for a draft just read from its entry. */
  save: boolean;
  /** An entry to empty: the new chat's, once its draft moved to the chat's id. */
  emptied?: string;
}

/** Composers on screen, told when a refused send's draft went back into an entry they show. */
const showingAgain = new Set<(key: string, refused: unknown) => void>();

/**
 * The one owner of a composer's draft under `key`: what it shows, kept as it changes.
 *
 * Another key (a switch to another conversation) shows that one's draft; the key a new chat got
 * once its first message named it (`carry`) keeps what is on screen. `handed`, when given, is
 * what the composer opens with, in place of anything kept.
 *
 * A send takes what it sends out of the composer and its entry at once (`take`), so a composer
 * opened again before the answer, or after a reload, never shows a message already on its way.
 * A refused send comes back (`settle`) in front of anything typed since: on screen, or into the
 * entry and any composer opened on it meanwhile when the person has left. Only a refusal after
 * a reload is lost: that send's promise went with the page.
 */
export default function useDraft<T>(key: string, kind: DraftKind<T>, handed?: T) {
  const [held, setHeld] = useState<Held<T>>(() => (handed ? { key, draft: handed, save: true } : { key, draft: kind.read(key), save: false }));
  /** The key a new chat moved to, from the key it had. */
  const carried = useRef<{ from: string; to: string } | null>(null);
  /** The key on screen; null once the composer is gone. */
  const onScreen = useRef<string | null>(key);

  let shown = held;
  if (held.key !== key) {
    const carry = carried.current?.from === held.key && carried.current.to === key;
    shown = carry ? { key, draft: held.draft, save: true, emptied: held.key } : { key, draft: kind.read(key), save: false };
    setHeld(shown);
  }

  // a handed draft replaces the one kept, which goes for good
  useEffect(() => {
    if (handed) kind.discard(key);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- on opening only
  useEffect(() => {
    if (held.emptied) kind.write(held.emptied, kind.empty);
    if (held.save) kind.write(held.key, held.draft);
  }, [held, kind]);
  useEffect(() => {
    onScreen.current = held.key;
    return () => {
      onScreen.current = null;
    };
  }, [held.key]);
  useEffect(() => {
    // merged into what is on screen, not read back: storage may have refused the write
    const showAgain = (key: string, refused: unknown) =>
      setHeld((now) => (now.key === key ? { key, draft: kind.merge(refused as T, now.draft), save: true } : now));
    showingAgain.add(showAgain);
    return () => void showingAgain.delete(showAgain);
  }, [kind]);

  const set = (update: SetStateAction<T>) =>
    setHeld((now) => ({ key: now.key, draft: typeof update === "function" ? (update as (draft: T) => T)(now.draft) : update, save: true }));

  /** `split` names what a send takes (`sent`) and what stays in the composer (`left`). */
  const take = (split: (draft: T) => { sent: T; left: T }): Taken<T> => {
    setHeld((now) => ({ key: now.key, draft: split(now.draft).left, save: true }));
    return { key: shown.key, owner: draftOwner(), draft: split(shown.draft).sent };
  };

  const settle = (taken: Taken<T>, sent: boolean) => {
    // sent: it left the composer and its entry when it was taken. Someone else signed in now: not theirs.
    if (sent || taken.owner !== draftOwner()) return;
    if (onScreen.current === taken.key) {
      setHeld((now) => (now.key !== taken.key ? now : { key: now.key, draft: kind.merge(taken.draft, now.draft), save: true }));
      return;
    }
    kind.write(taken.key, kind.merge(taken.draft, kind.read(taken.key)));
    showingAgain.forEach((showAgain) => showAgain(taken.key, taken.draft));
  };

  /** A new chat (`from`) got its id (`to`) from a send this composer made: its draft goes with it. */
  const carry = (from: string, to: string) => {
    if (onScreen.current === from) carried.current = { from, to };
  };

  return { draft: shown.draft, set, take, settle, carry };
}
